import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import AdminDashboardModal from "./dashboardModal";
import { useGetDashboardDataQuery } from "../api/dashboardApi";

const AdminDashboard = () => {
  const routes = all_routes;

  const [feeCollectionCharts, setFeeCollectionCharts] = useState<any[]>([]);
  const [courseStageCompletionCharts, setCourseStageCompletionCharts] =
    useState<any[]>([]);
  const [selectedTodayAttendanceCourse, setSelectedTodayAttendanceCourse] =
    useState(0);

  const {
    data: dashboardResponse,
    isLoading,
    error,
  } = useGetDashboardDataQuery();
  const dashboardData = dashboardResponse?.data?.dashboard;

  // Total overview across all courses
  const totalOverview = dashboardData?.key_metrics?.total_overview || (() => {
    const courses = dashboardData?.key_metrics?.courses;
    if (!courses || courses.length === 0) return null;
    const totalStudents = courses.reduce((sum: number, c: any) => sum + (c.total_students || 0), 0);
    const activeStudents = courses.reduce((sum: number, c: any) => sum + (c.active_students || 0), 0);
    const inactiveStudents = courses.reduce((sum: number, c: any) => sum + (c.inactive_students || 0), 0);
    const activePercentage = totalStudents > 0 ? `${((activeStudents / totalStudents) * 100).toFixed(2)}%` : "0%";

    let totalAttCount = 0;
    let totalPresent = 0;
    let totalAbsent = 0;
    dashboardData?.key_metrics?.current_month_attendance?.forEach((item: any) => {
      item?.data?.forEach((d: any) => {
        totalAttCount += d.total_students || 0;
        totalPresent += d.present || 0;
        totalAbsent += d.absent || 0;
      });
    });
    const attRate = totalAttCount > 0 ? `${((totalPresent / totalAttCount) * 100).toFixed(2)}%` : "0%";

    let totalFee = 0;
    let collectedFee = 0;
    let pendingFee = 0;
    dashboardData?.key_metrics?.fees_collection?.forEach((item: any) => {
      item?.data?.forEach((d: any) => {
        totalFee += d.total_fee || 0;
        collectedFee += d.collected_fee || 0;
        pendingFee += d.pending_fee || 0;
      });
    });
    const feePercentage = totalFee > 0 ? `${((collectedFee / totalFee) * 100).toFixed(2)}%` : "0%";

    return {
      total_students: totalStudents,
      active_students: activeStudents,
      inactive_students: inactiveStudents,
      active_percentage: activePercentage,
      attendance: {
        total_students: totalAttCount,
        present: totalPresent,
        absent: totalAbsent,
        present_percentage: attRate,
      },
      fees_collection: {
        total_fee: totalFee,
        collected_fee: collectedFee,
        pending_fee: pendingFee,
        total_collected_percentage: feePercentage,
      },
    };
  })();

  // console.log(dashboardData, 'dashboard data----');

  // Attendance trend chart configuration
  const [studentStatusChart, setStudentStatusChart] = useState<any>({
    chart: {
      height: 230,
      type: "area",
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100],
      },
    },
    colors: ["#3D5EE1"],
    series: [
      {
        name: "Attendance",
        data: [],
      },
    ],
    xaxis: {
      categories: [],
      labels: {
        style: {
          colors: "#5D6369",
          fontSize: "10px",
        },
        rotate: -45,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: function (val: any) {
          return val + "%";
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val + "%";
        },
      },
    },
  });

  // Course performance charts (one for each course)
  const [coursePerformanceCharts, setCoursePerformanceCharts] = useState<any[]>(
    []
  );


  // Update chart data when dashboard data is loaded
  useEffect(() => {
    if (dashboardData?.charts?.course_performance) {
      const performanceData = dashboardData.charts.course_performance;
      const stages = performanceData.data || [];
      const courses = performanceData.data?.courses || [];

      // Create chart configs for each course
      const chartConfigs = courses.map((course, index) => ({
        chart: {
          type: "line",
          height: 270,
          toolbar: {
            show: false,
          },
        },
        colors: index === 0 ? ["#3D5EE1"] : ["#28a745"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        series: [
          {
            name: course.name,
            data: course.data || [],
          },
        ],
        xaxis: {
          categories: stages,
          labels: {
            formatter: function (value: any) {
              // if the value already contains 'Stage'
              if (
                typeof value === "string" &&
                value.toLowerCase().includes("level")
              ) {
                return value;
              }
              return `Level ${value}`;
            },
          },
        },
        yaxis: {
          title: {
            text: "Average Score (%)",
          },
          min: 0,
          max: 100,
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
        },
        dataLabels: {
          enabled: false,
        },
        title: {
          text: course.name + " Performance",
          align: "left",
          style: {
            fontSize: "16px",
            fontWeight: "bold",
          },
        },
      }));

      setCoursePerformanceCharts(chartConfigs);
    }
  }, [dashboardData]);

  // Update fee collection charts when dashboard data is loaded
  useEffect(() => {
    if (dashboardData?.fee_collection_trends) {
      const trends = dashboardData.fee_collection_trends;

      // Create chart configs for each course's fee collection
      const chartConfigs = trends.map((course, index) => {
        const feeData = course.data.fee_collection;
        const seriesData = feeData?.map((series) => ({
          name: series.name,
          data: series.data,
        }));

        const categories = feeData?.[0]?.stages || [];

        return {
          chart: {
            type: "area",
            height: 270,
            toolbar: {
              show: false,
            },
          },
          colors: ["#28a745", "#E82646"],
          stroke: {
            curve: "smooth",
            width: 2,
          },
          fill: {
            type: "gradient",
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.7,
              opacityTo: 0.2,
              stops: [0, 90, 100],
            },
          },
          series: seriesData,
          xaxis: {
            categories: categories,
            labels: {
              formatter: function (value: any) {
                // if the value already contains 'Stage'
                if (
                  typeof value === "string" &&
                  value.toLowerCase().includes("level")
                ) {
                  return value;
                }
                return `Level ${value}`;
              },
            },
          },
          yaxis: {
            title: {
              text: "Amount (₹)",
            },
            labels: {
              formatter: function (val: any) {
                return "₹" + val.toFixed(0);
              },
            },
          },
          dataLabels: {
            enabled: false,
          },
          title: {
            text: course.name + " Fee Collection",
            align: "left",
            style: {
              fontSize: "16px",
              fontWeight: "bold",
            },
          },
        };
      });

      setFeeCollectionCharts(chartConfigs);
    }
  }, [dashboardData]);

  // Enrollment trend chart
  const [enrollmentTrendChart, setEnrollmentTrendChart] = useState<any>({
    chart: {
      height: 270,
      type: "bar",
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontSize: "14px",
      labels: {
        colors: "#5D6369",
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        endingShape: "rounded",
      },
    },
    colors: ["#3D5EE1", "#E9EDF4"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    grid: {
      padding: {
        left: -8,
      },
    },
    series: [
      {
        name: "Mental Arithmetic",
        data: [0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "Tiny Tots",
        data: [0, 0, 0, 0, 0, 0, 0],
      },
    ],
    xaxis: {
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    },
    yaxis: {
      tickAmount: 4,
      labels: {
        offsetX: -15,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val + " students";
        },
      },
    },
  });

  // Update enrollment trend chart when dashboard data is loaded
  useEffect(() => {
    if (dashboardData?.enrollment_trends?.data) {
      const enrollmentData = dashboardData.enrollment_trends.data;
      const courses = enrollmentData.course || [];
      const months = enrollmentData.months || [];

      // Create series data for each course
      const seriesData = courses.map((course) => ({
        name: course.name,
        data: course.data || [],
      }));

      // Update the chart config
      setEnrollmentTrendChart((prev: any) => ({
        ...prev,
        series: seriesData,
        xaxis: {
          ...prev.xaxis,
          categories: months,
          labels: {
            formatter: function (value: any) {
              return value;
            },
          },
        },
        colors: ["#3D5EE1", "#28a745", "#FFC107", "#E82646", "#6c757d"],
      }));
    }
  }, [dashboardData]);

  // Replace the existing useEffect for course stage completion with this one
  useEffect(() => {
    if (dashboardData?.charts?.course_stage_completion?.data) {
      const courses = dashboardData.charts?.course_stage_completion.data;

      // Get all unique stage names across all courses
      const allStageNames = new Set<string>();

      // Extract stage data from each course
      courses.forEach((course) => {
        course?.data?.stages?.forEach((stage) => {
          allStageNames.add(stage.name);
        });
      });

      // Sort stage names (assuming they're numbered like "Stage 1", "Stage 2", etc.)
      const sortedStageNames = Array.from(allStageNames).sort((a, b) => {
        const numA = parseInt(a.match(/\d+/)?.[0] || "0");
        const numB = parseInt(b.match(/\d+/)?.[0] || "0");
        return numA - numB;
      });

      // Create series data
      const seriesData = courses.map((course) => {
        // For each course, collect completion data across all stages
        const stageData = sortedStageNames.map((stageName) => {
          const stageInfo = course.data.stages?.find(
            (s) => s.name === stageName
          );
          if (!stageInfo) return 0;

          const [completed, total] = stageInfo.data ?? [0, 0];
          // Calculate completion percentage, handling zero case
          return total > 0 ? Math.round((completed / total) * 100) : 0;
        });

        return {
          name: course.name,
          data: stageData,
        };
      });

      // Create unified chart config
      const chartConfig = {
        chart: {
          type: "bar",
          height: 270,
          toolbar: {
            show: false,
          },
          stacked: false,
          fontFamily: "inherit",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
          },
        },
        colors: ["#3D5EE1", "#28a745", "#FFC107", "#E82646", "#6c757d"],
        plotOptions: {
          bar: {
            columnWidth: "55%",
            endingShape: "rounded",
            borderRadius: 4,
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabled: false,
          formatter: function (val: any) {
            return val + "%";
          },
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#304758"],
          },
        },
        stroke: {
          width: 2,
          colors: ["transparent"],
        },
        series: seriesData,
        xaxis: {
          categories: sortedStageNames,
          labels: {
            formatter: function (value: any) {
              return value;
            },
            style: {
              colors: "#5D6369",
              fontSize: "12px",
            },
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          title: {
            text: "Completion (%)",
            style: {
              color: "#5D6369",
            },
          },
          labels: {
            formatter: function (val: any) {
              return val + "%";
            },
            style: {
              colors: "#5D6369",
            },
          },
          min: 0,
          max: 100,
        },
        tooltip: {
          y: {
            formatter: function (val: any, opts: any) {
              const seriesIndex = opts.seriesIndex;
              const dataPointIndex = opts.dataPointIndex;
              const stageName = sortedStageNames[dataPointIndex];
              const course = courses[seriesIndex];

              const stageInfo = course.data.stages?.find(
                (s) => s.name === stageName
              );
              if (!stageInfo) return `${val}% completion`;

              const [completed, total] = stageInfo.data ?? [0, 0];
              return `${val}% completion (${completed}/${total} students)`;
            },
          },
          shared: true,
          intersect: false,
          theme: "light",
        },
        fill: {
          opacity: 1,
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.2,
            inverseColors: false,
            opacityFrom: 0.85,
            opacityTo: 0.85,
            stops: [0, 100],
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "14px",
          offsetY: 0,
          labels: {
            colors: "#5D6369",
          },
          markers: {
            width: 12,
            height: 12,
            radius: 12,
          },
        },
        grid: {
          borderColor: "#e7e7e7",
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
      };

      setCourseStageCompletionCharts([chartConfig]);
    }
  }, [dashboardData]);

  // Update studentStatusChart when attendance course changes
  useEffect(() => {
    if (
      dashboardData?.attendance_today &&
      dashboardData.attendance_today[selectedTodayAttendanceCourse]
    ) {
      const attendanceData =
        dashboardData.attendance_today[selectedTodayAttendanceCourse];
      const percentages = attendanceData.attendance_percentage || [];
      const legends = attendanceData.legend || [];

      setStudentStatusChart((prev: any) => ({
        ...prev,
        series: [
          {
            name: "Attendance",
            data: percentages,
          },
        ],
        xaxis: {
          ...prev.xaxis,
          categories: legends,
        },
      }));
    }
  }, [dashboardData, selectedTodayAttendanceCourse]);

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div
              className="col-12 d-flex justify-content-center align-items-center"
              style={{ minHeight: "60vh" }}
            >
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading dashboard data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error message if API request fails
  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <h4 className="alert-heading">Error loading dashboard data</h4>
                <p>
                  An error occurred while loading dashboard data. Please try
                  refreshing the page.
                </p>
                <hr />
                <p className="mb-0">
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => window.location.reload()}
                  >
                    <i className="ti ti-refresh me-1"></i> Refresh Page
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {dashboardData?.name || "Dashboard"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {dashboardData?.name || "Dashboard"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              <div className="mb-2 d-flex gap-2">
                <Link
                  to={routes.quickRegister}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-bolt me-2" />
                  Quick Register
                </Link>
                <Link
                  to={routes.addStudent}
                  className="btn btn-outline-primary d-flex align-items-center"
                >
                  <i className="ti ti-forms me-2" />
                  Full Registration
                </Link>
              </div>
              <div className="mb-2">
                <Link
                  to={routes.collectFees}
                  className="btn btn-light d-flex align-items-center"
                >
                  Fees Management
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Welcome Card */}
          <div className="row">
            <div className="col-md-12">
              <div className="card bg-dark">
                <div className="overlay-img">
                  <ImageWithBasePath
                    src="assets/img/bg/shape-04.png"
                    alt="img"
                    className="img-fluid shape-01"
                  />
                  <ImageWithBasePath
                    src="assets/img/bg/shape-01.png"
                    alt="img"
                    className="img-fluid shape-02"
                  />
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-xl-center justify-content-xl-between flex-xl-row flex-column">
                    <div className="mb-3 mb-xl-0">
                      <div className="d-flex align-items-center flex-wrap mb-2">
                        <h1 className="text-white me-2">
                          {dashboardData?.active_session}
                        </h1>
                      </div>
                      <p className="text-white">
                        Course management and student performance analytics
                      </p>
                    </div>
                    <div className="text-white custom-text-white text-end">
                      <div>
                        <i className="ti ti-refresh me-1" />
                        Updated on: {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Overview (All Courses) */}
          {totalOverview && (
            <div className="mb-4">
              <h4 className="mb-3 text-primary">Total Overview</h4>
              <div className="row">
                {/* Total Students */}
                <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                  <div className="card flex-fill animate-card border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-xl bg-success-transparent me-2 p-1">
                          <i className="ti ti-users text-success fs-24"></i>
                        </div>
                        <div className="overflow-hidden flex-fill">
                          <div className="d-flex align-items-center justify-content-between">
                            <h2 className="counter">
                              <CountUp end={totalOverview.total_students || 0} />
                            </h2>
                          </div>
                          <p>Total Students <span className="fs-12 text-muted">(All Courses)</span></p>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                        <span>
                          Active: {totalOverview.active_students || 0} (
                          {totalOverview.active_percentage || "0%"})
                        </span>
                        <span>Inactive: {totalOverview.inactive_students || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Attendance */}
                {totalOverview.attendance && (
                  <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                    <div className="card flex-fill animate-card border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-xl me-2 bg-info-transparent p-1">
                            <i className="ti ti-user-check fs-24 text-info"></i>
                          </div>
                          <div className="overflow-hidden flex-fill">
                            <div className="d-flex align-items-center justify-content-between">
                              <h2>
                                {totalOverview.attendance.present_percentage || "0%"}
                              </h2>
                            </div>
                            <p>Attendance Rate <span className="fs-12 text-muted">(Current Month)</span></p>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                          <span>
                            Present: {totalOverview.attendance.present || 0}
                          </span>
                          <span>
                            Absent: {totalOverview.attendance.absent || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Total Fees Collection */}
                {totalOverview.fees_collection && (
                  <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                    <div className="card flex-fill animate-card border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-xl me-2 bg-warning-transparent p-1">
                            <i className="ti ti-receipt fs-24 text-warning"></i>
                          </div>
                          <div className="overflow-hidden flex-fill">
                            <div className="d-flex align-items-center justify-content-between">
                              <h2>
                                ₹
                                {(
                                  totalOverview.fees_collection.collected_fee || 0
                                ).toLocaleString("en-IN")}
                              </h2>
                              <span className="badge bg-warning">
                                {totalOverview.fees_collection.total_collected_percentage || "0%"}
                              </span>
                            </div>
                            <p>Total Fees Collection</p>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                          <span>
                            Collected: ₹
                            {(
                              totalOverview.fees_collection.collected_fee || 0
                            ).toLocaleString("en-IN")}
                          </span>
                          <span>
                            Pending: ₹
                            {(
                              totalOverview.fees_collection.pending_fee || 0
                            ).toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Key Metrics Grouped by Course */}
          {dashboardData?.key_metrics?.courses?.map((course: any, index: number) => {
            const attendanceCourse = dashboardData.key_metrics.current_month_attendance?.[index];
            const feeCourse = dashboardData.key_metrics.fees_collection?.[index];

            return (
              <div key={index} className="mb-4">
                <h4 className="mb-3 text-primary">{course.name} Overview</h4>
                <div className="row">
                  {/* Students */}
                  <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                    <div className="card flex-fill animate-card border-0">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <div
                            className={`avatar avatar-xl ${index === 0
                              ? "bg-primary-transparent"
                              : "bg-danger-transparent"
                              } me-2 p-1`}
                          >
                            <i
                              className={`ti ${index === 0
                                ? "ti-brain text-purple"
                                : "ti-pencil text-orange"
                                } fs-24`}
                            ></i>
                          </div>
                          <div className="overflow-hidden flex-fill">
                            <div className="d-flex align-items-center justify-content-between">
                              <h2 className="counter">
                                <CountUp end={course?.total_students || 0} />
                              </h2>
                            </div>
                            <p>Total Students</p>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                          <span>
                            Active: {course?.active_students || 0} (
                            {course?.active_percentage || 0})
                          </span>
                          <span>Inactive: {course?.inactive_students || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Attendance */}
                  {attendanceCourse && (
                    <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                      <div className="card flex-fill animate-card border-0">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-xl me-2 bg-info-transparent p-1">
                              <i className="ti ti-user-check fs-24 text-info"></i>
                            </div>
                            <div className="overflow-hidden flex-fill">
                              <div className="d-flex align-items-center justify-content-between">
                                <h2>
                                  {attendanceCourse?.data?.[0]?.present_percentage || "0%"}
                                </h2>
                              </div>
                              <p>Attendance Rate <span className="fs-12 text-muted">(Current Month)</span></p>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                            <span>
                              Present: {attendanceCourse?.data?.[0]?.present || 0}
                            </span>
                            <span>
                              Absent: {attendanceCourse?.data?.[0]?.absent || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fees Collection */}
                  {feeCourse && (
                    <div className="col-xxl-4 col-md-4 col-sm-6 d-flex">
                      <div className="card flex-fill animate-card border-0">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <div className="avatar avatar-xl me-2 bg-warning-transparent p-1">
                              <i className="ti ti-receipt fs-24 text-warning"></i>
                            </div>
                            <div className="overflow-hidden flex-fill">
                              <div className="d-flex align-items-center justify-content-between">
                                <h2>
                                  ₹
                                  {(
                                    feeCourse?.data?.[0]?.collected_fee || 0
                                  ).toLocaleString("en-IN")}
                                </h2>
                                <span className="badge bg-warning">
                                  {feeCourse?.data?.[0]?.total_collected_percentage || "0%"}
                                </span>
                              </div>
                              <p>Fees Collection</p>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                            <span>
                              Collected: ₹
                              {(
                                feeCourse?.data?.[0]?.collected_fee || 0
                              ).toLocaleString("en-IN")}
                            </span>
                            <span>
                              Pending: ₹
                              {(
                                feeCourse?.data?.[0]?.pending_fee || 0
                              ).toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Course Performance & Fee Collection Row */}
          <div className="row">
            {/* Course Performance Charts */}
            {coursePerformanceCharts.length > 0 ? (
              coursePerformanceCharts.map((chartConfig, index) => (
                <div className="col-lg-6 d-flex" key={index}>
                  <div className="card flex-fill mb-4">
                    <div className="card-body">
                      <ReactApexChart
                        id={`course-performance-chart-${index}`}
                        options={chartConfig}
                        series={chartConfig.series}
                        type="line"
                        height={270}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <>
                {/* Loading placeholders for charts */}
                <div className="col-lg-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h4 className="card-title">Course Performance</h4>
                    </div>
                    <div
                      className="card-body d-flex align-items-center justify-content-center"
                      style={{ height: "270px" }}
                    >
                      <p>Loading chart data...</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-header">
                      <h4 className="card-title">Course Performance</h4>
                    </div>
                    <div
                      className="card-body d-flex align-items-center justify-content-center"
                      style={{ height: "270px" }}
                    >
                      <p>Loading chart data...</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Fee Collection Charts - Dynamically rendered for each course */}
            {feeCollectionCharts.length > 0 ? (
              feeCollectionCharts.map((chartConfig, index) => (
                <div className="col-lg-6 d-flex" key={`fee-chart-${index}`}>
                  <div className="card flex-fill mb-4">
                    <div className="card-body">
                      <ReactApexChart
                        id={`fee-collection-chart-${index}`}
                        options={chartConfig}
                        series={chartConfig.series}
                        type="area"
                        height={270}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-lg-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-header">
                    <h4 className="card-title">Fee Collection Trends</h4>
                  </div>
                  <div
                    className="card-body d-flex align-items-center justify-content-center"
                    style={{ height: "270px" }}
                  >
                    <p>Loading fee collection data...</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="row">
            {/* Enrollment Trends */}
            <div className="col-lg-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Enrollment Trends</h4>
                </div>
                <div className="card-body">
                  <ReactApexChart
                    id="enrollment-trend-chart"
                    options={enrollmentTrendChart}
                    series={enrollmentTrendChart.series}
                    type="bar"
                    height={270}
                  />
                </div>
              </div>
            </div>
            {/* /Enrollment Trends */}

            {/* Course Stage Completion Chart */}
            <div className="col-lg-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header">
                  <h4 className="card-title">Level Completion by Course</h4>
                </div>
                <div className="card-body">
                  {courseStageCompletionCharts.length > 0 ? (
                    <ReactApexChart
                      id="course-stage-chart"
                      options={courseStageCompletionCharts[0]}
                      series={courseStageCompletionCharts[0].series}
                      type="bar"
                      height={270}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "270px" }}
                    >
                      <p>Loading course level data...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Attendance Trends */}
            <div className="col-xxl-6 col-xl-5 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Attendance Trends</h4>
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="bg-white dropdown-toggle"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-filter me-1" />
                      {dashboardData?.attendance_today?.[
                        selectedTodayAttendanceCourse
                      ]?.name || "All Courses"}
                    </Link>
                    <ul className="dropdown-menu mt-2 p-3">
                      {dashboardData?.attendance_today?.map((course: any, index: number) => (
                        <li key={index}>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedTodayAttendanceCourse(index);
                            }}
                          >
                            {course.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  <div className="text-center">
                    {(dashboardData?.attendance_today?.[
                      selectedTodayAttendanceCourse
                    ]?.attendance_percentage?.length ?? 0) > 0 ? (
                      <>
                        <ReactApexChart
                          id="student-status-chart"
                          className="mb-4"
                          options={studentStatusChart}
                          series={studentStatusChart.series}
                          type="area"
                          height={230}
                        />
                        <Link
                          to={routes.studentAttendance}
                          className="btn btn-primary mt-2"
                        >
                          <i className="ti ti-users me-1" />
                          View Attendance Records
                        </Link>
                      </>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ height: "230px" }}
                      >
                        <div className="avatar avatar-xl bg-primary-transparent mb-3">
                          <i className="ti ti-calendar-off fs-24 text-primary"></i>
                        </div>
                        <h5 className="text-muted mb-2">
                          No Attendance Records Found
                        </h5>
                        <p className="text-muted mb-3">
                          Attendance data for this course has not been recorded yet.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* /Attendance Trends */}

            {/* Quick Links */}
            <div className="col-xxl-6 col-xl-4 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Quick Actions</h4>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6 mb-3">
                      <Link
                        to={routes.batchTimingList}
                        className="d-block bg-primary-transparent p-3 text-center rounded h-100"
                      >
                        <div className="avatar avatar-lg border p-1 border-primary rounded-circle mb-2 mx-auto">
                          <span className="d-inline-flex align-items-center justify-content-center w-100 h-100 bg-primary rounded-circle">
                            <i className="ti ti-clock" />
                          </span>
                        </div>
                        <p className="text-dark">Batch Timings</p>
                      </Link>
                    </div>
                    <div className="col-6 mb-3">
                      <Link
                        to={routes.generateInvoices}
                        className="d-block bg-success-transparent p-3 text-center rounded h-100"
                      >
                        <div className="avatar avatar-lg border p-1 border-success rounded-circle mb-2 mx-auto">
                          <span className="d-inline-flex align-items-center justify-content-center w-100 h-100 bg-success rounded-circle">
                            <i className="ti ti-license" />
                          </span>
                        </div>
                        <p className="text-dark">Generate Invoices</p>
                      </Link>
                    </div>
                    <div className="col-6 mb-3">
                      <Link
                        to={routes.notificationList}
                        className="d-block bg-warning-transparent p-3 text-center rounded h-100"
                      >
                        <div className="avatar avatar-lg border p-1 border-warning rounded-circle mb-2 mx-auto">
                          <span className="d-inline-flex align-items-center justify-content-center w-100 h-100 bg-warning rounded-circle">
                            <i className="ti ti-bell" />
                          </span>
                        </div>
                        <p className="text-dark">Notification</p>
                      </Link>
                    </div>
                    <div className="col-6 mb-3">
                      <Link
                        to={routes.studentList}
                        className="d-block bg-danger-transparent p-3 text-center rounded h-100"
                      >
                        <div className="avatar avatar-lg border p-1 border-danger rounded-circle mb-2 mx-auto">
                          <span className="d-inline-flex align-items-center justify-content-center w-100 h-100 bg-danger rounded-circle">
                            <i className="ti ti-list" />
                          </span>
                        </div>
                        <p className="text-dark">Student List</p>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Quick Links */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <AdminDashboardModal />
    </>
  );
};

export default AdminDashboard;
